import { useEffect, useRef, useState } from 'react';
import { useHls, getActiveHls } from './useHls';
import { useDash } from './useDash';
import { isDashUrl } from '@/lib/streamType';
import { PlayerControls } from './PlayerControls';
import { usePlayerStore } from '@/store/usePlayerStore';
import { useSettingsStore } from '@/store/useSettingsStore';
import type { Channel } from '@/lib/channels';

interface VideoPlayerProps {
  channel: Channel;
}

const HIDE_CONTROLS_MS = 2500;

export function VideoPlayer({ channel }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [showControls, setShowControls] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const hideTimer = useRef<number | null>(null);

  const setIsPlaying = usePlayerStore((s) => s.setIsPlaying);
  const setCurrentTime = usePlayerStore((s) => s.setCurrentTime);
  const setDuration = usePlayerStore((s) => s.setDuration);
  const setBufferedEnd = usePlayerStore((s) => s.setBufferedEnd);
  const setLive = usePlayerStore((s) => s.setLive);
  const setLatency = usePlayerStore((s) => s.setLatency);

  // Both hooks are always called to satisfy React's rules of hooks.
  // Only the matching one activates based on the URL extension.
  const isDash = isDashUrl(channel.url);
  useHls(videoRef, isDash ? null : channel.url);
  useDash(videoRef, isDash ? channel.url : null);

  // On mount and on channel change, set the persisted volume and
  // playback rate. The <video> element starts muted (JSX `muted`
  // attribute) so the browser allows autoplay without a user gesture.
  // The persisted mute state is synced after play starts (see
  // handlePlay below). If we set video.muted=isMuted here and isMuted
  // is false, the browser would reject unmuted autoplay.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const { volume, playbackRate } = useSettingsStore.getState();
    video.volume = volume;
    video.playbackRate = playbackRate;
  }, [channel.id]);

  // Wire up video element events.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hasPlayed = false;
    let pendingPlay = false;

    const handlePlay = () => {
      setIsPlaying(true);
      pendingPlay = false;
      hasPlayed = true;
      // Playback started — safe to sync the persisted mute state now
      // (browser won't reject muted→unmuted after play is underway).
      const { isMuted } = useSettingsStore.getState();
      if (video.muted !== isMuted) video.muted = isMuted;
    };

    const handlePause = () => setIsPlaying(false);
    const handleVolume = () => {
      // User dragged the OS volume slider or the element-level controls.
      // Mirror to the settings store so persistence picks it up.
      useSettingsStore.getState().setVolume(video.volume);
      if (video.muted !== useSettingsStore.getState().isMuted) {
        useSettingsStore.getState().setMuted(video.muted);
      }
    };

    const tryPlay = () => {
      // After the first successful play, user-initiated pauses must be
      // respected — don't re-autoplay when canplay/loadeddata fire.
      if (hasPlayed) return;
      if (pendingPlay) return;
      if (!video.paused) return;
      pendingPlay = true;
      video.play().catch((err: unknown) => {
        pendingPlay = false;
        // AbortError is expected when a new loadSource interrupts
        // an in-flight play — swallow it silently.
        if (!(err instanceof Error && err.name === 'AbortError')) {
          console.warn('Autoplay failed:', err);
        }
      });
    };

    // Start playback once the source is ready.
    video.addEventListener('canplay', tryPlay);
    video.addEventListener('loadeddata', tryPlay);

    // Optimistic play() — the video is already muted so the browser's
    // autoplay policy allows it. If no data is buffered yet the
    // browser queues the request and resolves it once data arrives.
    // The canplay/loadeddata handlers above serve as fallback.
    tryPlay();

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      const hls = getActiveHls();
      if (hls && hls.latency > 0) {
        setLatency(Math.round(hls.latency));
      }
    };

    const handleDurationChange = () => {
      const dur = video.duration;
      setDuration(dur);
      setLive(dur === Infinity);
    };

    const handleProgress = () => {
      if (video.buffered.length > 0) {
        setBufferedEnd(video.buffered.end(video.buffered.length - 1));
      }
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('volumechange', handleVolume);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('durationchange', handleDurationChange);
    video.addEventListener('progress', handleProgress);

    return () => {
      video.removeEventListener('canplay', tryPlay);
      video.removeEventListener('loadeddata', tryPlay);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('volumechange', handleVolume);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('durationchange', handleDurationChange);
      video.removeEventListener('progress', handleProgress);
    };
  }, [channel.id, setIsPlaying, setCurrentTime, setDuration, setBufferedEnd, setLive, setLatency]);

  // Track fullscreen state.
  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Media Session API: surfaces the stream to OS-level controls
  // (lock screen, headphones, media keys, browser tab favicon).
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    const artwork = channel.logo
      ? [{ src: channel.logo, sizes: '512x512', type: 'image/png' }]
      : [];

    navigator.mediaSession.metadata = new MediaMetadata({
      title: channel.name,
      artist: channel.country,
      album: channel.category,
      artwork,
    });

    const video = videoRef.current;
    if (video) {
      const play = () => video.play();
      const pause = () => video.pause();
      const seekForward = () => {
        video.currentTime = Math.min(video.duration || 0, video.currentTime + 10);
      };
      const seekBackward = () => {
        video.currentTime = Math.max(0, video.currentTime - 10);
      };
      const stop = () => {
        video.pause();
        video.currentTime = 0;
      };

      navigator.mediaSession.setActionHandler('play', play);
      navigator.mediaSession.setActionHandler('pause', pause);
      navigator.mediaSession.setActionHandler('seekforward', seekForward);
      navigator.mediaSession.setActionHandler('seekbackward', seekBackward);
      navigator.mediaSession.setActionHandler('stop', stop);
    }

    return () => {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.metadata = null;
        ['play', 'pause', 'seekforward', 'seekbackward', 'stop'].forEach((a) => {
          try {
            navigator.mediaSession.setActionHandler(a as MediaSessionAction, null);
          } catch {
            // Some actions aren't supported on every platform; ignore.
          }
        });
      }
    };
  }, [channel.id, channel.name, channel.country, channel.category, channel.logo]);

  // Auto-hide controls when the mouse stops moving.
  // We keep them visible while paused so the user can hit play again.
  const resetHideTimer = () => {
    if (hideTimer.current) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
    setShowControls(true);
    if (usePlayerStore.getState().isPlaying) {
      hideTimer.current = window.setTimeout(() => setShowControls(false), HIDE_CONTROLS_MS);
    }
  };

  useEffect(() => {
    // When play/pause state changes, refresh the timer.
    resetHideTimer();
    // Subscribe imperatively so the effect re-runs on transitions.
    return usePlayerStore.subscribe((s, prev) => {
      if (s.isPlaying !== prev.isPlaying) resetHideTimer();
    });
  }, []);

  const handleContainerClick = (e: React.MouseEvent) => {
    // Click on the container (not on a button) toggles play/pause.
    if ((e.target as HTMLElement).closest('button')) return;
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      // play() returns a promise that rejects with NotSupportedError
      // when the element has no source, or with AbortError when a
      // new load interrupts an in-flight play. Swallow those — the
      // error overlay will surface anything the user needs to know.
      video.play().catch((err: unknown) => {
        if (err instanceof Error && err.name !== 'AbortError') {
          console.warn('Play failed:', err);
        }
      });
    } else {
      video.pause();
    }
    resetHideTimer();
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const isLeft = x < rect.width / 2;
    const video = videoRef.current;
    if (!video) return;
    if (isLeft) {
      video.currentTime = Math.max(0, video.currentTime - 10);
    } else {
      video.currentTime = Math.min(video.duration || 0, video.currentTime + 10);
    }
    resetHideTimer();
  };

  return (
    <div
      ref={containerRef}
      data-player-container
      onMouseMove={resetHideTimer}
      onMouseLeave={() => {
        if (usePlayerStore.getState().isPlaying) setShowControls(false);
      }}
      onClick={handleContainerClick}
      onDoubleClick={handleDoubleClick}
      className={`relative bg-black aspect-video max-h-[65vh] w-full group select-none ${isFullscreen ? '' : 'rounded-md overflow-hidden'
        }`}
    >
      <video
        ref={videoRef}
        playsInline
        muted
        // Muted by default so the browser allows autoplay without a
        // user gesture (needed for shared deep-links). The persisted
        // mute state is synced in the `play` event handler after
        // playback starts.
        controls={false}
        poster={channel.logo}
        className="w-full h-full object-contain"
      />

      {/* Controls */}
      <PlayerControls
        videoRef={videoRef}
        visible={showControls}
        isFullscreen={isFullscreen}
        containerRef={containerRef}
      />
    </div>
  );
}
